import {
  ELECTRICITY_RATE_FLAT_CONFIRM_ABOVE_CENTS,
  ELECTRICITY_RATE_FLAT_MAX_CENTS,
  ELECTRICITY_RATE_TOU_MAX_CENTS,
  homeRatesNeedingConfirmation,
  rateToHundredthCent,
  type HomeFlatRates,
} from '@evi/shared';
import { formatMoney } from './money';

/**
 * The homeowner's self-entered utility rate on the Site electricity
 * constants (Warren, 2026-09-27): "The homeowner's self-entered rate goes
 * on the same constant, 40 flat, 70 on a TOU window." The TOU cap was
 * raised to 80 the same day ("It is a typo guard, not the harm guard ...
 * Flat stays at 40.").
 *
 * The flat cap is 60 since Warren's ruling of 21 October 2026, night
 * ("Change: the Mcp tool's cap and a homeowner's own rate cap go to 60
 * cents, like the rest. The location form stores rates to a hundredth of a
 * cent, so 43.63 stays 43.63."), with the same second step above 40 as
 * every other writer (homeRateConfirmCents, components/HomeRateConfirmStep):
 *
 *   flat   the flat rate and the TOU base rate: 60¢, above 40¢ with the
 *          second step. Not checked on a tiered save, which re-sends the
 *          stored base the tiered editor hides (the API skips it the same
 *          way).
 *   window a time-of-use window: 80¢, never asked.
 *   tier   a tiered bracket: 60¢, above 40¢ with the second step.
 *
 * PATCH /me/home/rate refuses the same rates. Home Settings and the earn
 * walk check first so the owner sees why inline, in dollars, before the
 * round trip. Rates here are cents to a hundredth of a cent: the $ fields
 * take up to four decimals ($0.4363 is 43.63¢), as the Site's rate does.
 */
export type UtilityRateShape = 'flat' | 'window' | 'tier';

export function utilityRateMaxCents(shape: UtilityRateShape): number {
  return shape === 'window' ? ELECTRICITY_RATE_TOU_MAX_CENTS : ELECTRICITY_RATE_FLAT_MAX_CENTS;
}

/** A rate above its cap. Blank or invalid (null) is not "over": the form handles it separately. */
export function isUtilityRateOverCeiling(cents: number | null | undefined, shape: UtilityRateShape): boolean {
  return typeof cents === 'number' && Number.isFinite(cents) && cents > utilityRateMaxCents(shape);
}

/** The cap in dollars, for a $ input's native max: 0.6 or 0.8. */
export function utilityCeilingInDollars(shape: UtilityRateShape): number {
  return utilityRateMaxCents(shape) / 100;
}

/** The cap as the rate field writes it, in the home's currency (item 3):
 *  "$0.60" or "$0.80" for USD, "CA$0.60" for CAD. No currency reads USD. */
export function utilityCeilingLabel(
  shape: UtilityRateShape,
  currency?: string | null,
  locale?: string | null,
): string {
  return formatMoney(utilityRateMaxCents(shape), currency, locale);
}

/**
 * Typed dollars ("0.4363", "0,44") to cents to a hundredth of a cent (43.63, 44). Null when blank, unparseable or
 * negative. Rounded on the cents, float noise first, so 0.145 is 14.5 and 0.43635 is 43.64.
 */
export function utilityDollarsToCents(text: string): number | null {
  const trimmed = text.trim().replace(',', '.');
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) && n >= 0 ? rateToHundredthCent(n * 100) : null;
}

/** Stored cents back into the $ field: two decimals for whole cents ("0.17"), up to four otherwise ("0.4363"). */
export function utilityCentsToDollars(cents: number | null | undefined): string {
  if (cents == null) return '';
  const hundredths = rateToHundredthCent(cents);
  return Number.isInteger(hundredths) ? (hundredths / 100).toFixed(2) : String(Number((hundredths / 100).toFixed(4)));
}

/** The threshold of the second step, 40¢, for its sentence. */
export const UTILITY_RATE_CONFIRM_ABOVE_CENTS = ELECTRICITY_RATE_FLAT_CONFIRM_ABOVE_CENTS;

/**
 * Whether a save shows the second step first, and for which rate (Warren, 21 October 2026, night): the first flat
 * per-kWh rate in it above 40¢ and at most 60¢ that is not the one already saved in its place (homeRatesNeedingConfirmation,
 * the list PATCH /me/home/rate asks for). Null: save straight away.
 */
export function homeRateConfirmCents(save: HomeFlatRates, saved?: HomeFlatRates | null): number | null {
  return homeRatesNeedingConfirmation(save, saved)[0] ?? null;
}
